import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { NovuProvider } from "@novu/react";
import { useNotifications } from "src/hooks/useNotifications";
import { useAuthInfo } from "@propelauth/react";
import { useTranslation } from "react-i18next";
import NotificationPermissionModal from "src/app/shared-components/NotificationPermissionModal";
import { useAppSelector } from "app/store/hooks";
import { selectUser } from "src/app/auth/user/store/userSlice";
import { 
  shouldShowNotificationModal,
  markModalAsDismissed,
  isMobileDevice,
  getNotificationStateDescription,
  getDeviceInfo
} from "src/app/utils/notificationUtils";

interface NotificationContextType {
  permission: NotificationPermission;
  isSupported: boolean;
  fcmToken: string | null;
  notifications: any[];
  unreadCount: number;
  loading: boolean;
  requestPermission: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markAsSeen: (notificationId: number) => Promise<void>;
  markAllAsSeen: () => Promise<void>;
  showPermissionModal: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within NotificationProvider"
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const user = useAppSelector(selectUser);
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [hasAttemptedPermission, setHasAttemptedPermission] = useState(false);
  const [isCheckingModalState, setIsCheckingModalState] = useState(false);

  // Enhanced auto-request permission logic with private browsing and dismissal handling
  useEffect(() => {
    const checkAndShowModal = async () => {
      // Only check if user is logged in, notifications are supported, and we haven't attempted yet
      if (!user || !notifications.isSupported || hasAttemptedPermission || isCheckingModalState) {
        return;
      }

      setIsCheckingModalState(true);
      
      try {
        console.log('🔔 Checking notification modal conditions...');
        console.log('📱 Current state:', getNotificationStateDescription(
          notifications.permission, 
          notifications.isSupported
        ));
        console.log('🖥️ Device information:', getDeviceInfo());
        
        // Use enhanced logic to determine if modal should show
        const shouldShow = await shouldShowNotificationModal(
          notifications.permission,
          notifications.isSupported
        );
        
        if (shouldShow) {
          console.log('✅ Showing notification permission modal');
          // Short delay to let the UI load
          const timer = setTimeout(() => {
            setShowPermissionModal(true);
            setHasAttemptedPermission(true);
            setIsCheckingModalState(false);
          }, 1000);
          
          return () => clearTimeout(timer);
        } else {
          console.log('❌ Skipping notification permission modal');
          setHasAttemptedPermission(true);
          setIsCheckingModalState(false);
        }
      } catch (error) {
        console.error('Error checking notification modal state:', error);
        setIsCheckingModalState(false);
      }
    };

    checkAndShowModal();
  }, [
    user,
    notifications.isSupported,
    notifications.permission,
    hasAttemptedPermission,
    isCheckingModalState,
  ]);

  const handleRequestPermission = async () => {
    // This will trigger the browser's native permission dialog
    await notifications.requestPermission();
    if (notifications.permission === "granted") {
      setShowPermissionModal(false);
    }
  };

  const handleRetryPermission = async () => {
    if (notifications.permission === "denied") {
      // For denied permissions, check if user has enabled it in browser
      const currentPermission = Notification.permission;
      if (currentPermission === "granted") {
        // User has enabled it, request permission
        await notifications.requestPermission();
        setShowPermissionModal(false);
      } else {
        // Still denied, show help
        alert(t("REFRESH_PAGE_AFTER_ENABLING"));
      }
    } else {
      // Request permission
      await notifications.requestPermission();
      if (notifications.permission === "granted") {
        setShowPermissionModal(false);
      }
    }
  };

  const handleCloseModal = useCallback(() => {
    setShowPermissionModal(false);
    // Mark modal as dismissed to prevent it from showing again
    markModalAsDismissed();
    console.log('🔕 Notification modal dismissed by user');
    // Note: We don't set hasAttemptedPermission to false here
    // so users can still manually enable via the notification button
  }, []);

  const handleDismissModal = useCallback(() => {
    setShowPermissionModal(false);
    // Mark modal as dismissed to prevent it from showing again
    markModalAsDismissed();
    console.log('🔕 Notification modal dismissed permanently');
  }, []);

  const handleShowPermissionModal = () => {
    setShowPermissionModal(true);
  };

  // Get subscriber ID from PropelAuth user
  // Only use authenticated users for Novu - avoid creating anonymous subscribers
  const subscriberId = user?.uid;

  const orgIds = Object.keys(user.org_id_to_org_member_info);
  const firstOrgId = orgIds[0];

  // Only render NovuProvider if user is authenticated
  if (!subscriberId) {
    return (
      <NotificationContext.Provider
        value={{
          ...notifications,
          showPermissionModal: handleShowPermissionModal,
        }}
      >
        {children}
        <NotificationPermissionModal
          open={showPermissionModal}
          onRetry={handleRetryPermission}
          onClose={handleCloseModal}
          onDismiss={handleDismissModal}
          permission={notifications.permission}
          isSupported={notifications.isSupported}
          onRequestPermission={handleRequestPermission}
          isMobile={isMobileDevice()}
        />
      </NotificationContext.Provider>
    );
  }

  return (
    // @ts-ignore - Novu provider types may be incompatible
    <NovuProvider
      applicationIdentifier="lJqtY_URuYl5"
      subscriber={`${firstOrgId}_${subscriberId}`}
      backendUrl={import.meta.env.NOVU_BACKEND_URL || "https://eu.api.novu.co"}
      socketUrl={import.meta.env.NOVU_SOCKET_URL || "https://eu.ws.novu.co"}
    >
      <NotificationContext.Provider
        value={{
          ...notifications,
          showPermissionModal: handleShowPermissionModal,
        }}
      >
        {children}
        <NotificationPermissionModal
          open={showPermissionModal}
          onRetry={handleRetryPermission}
          onClose={handleCloseModal}
          onDismiss={handleDismissModal}
          permission={notifications.permission}
          isSupported={notifications.isSupported}
          onRequestPermission={handleRequestPermission}
          isMobile={isMobileDevice()}
        />
      </NotificationContext.Provider>
    </NovuProvider>
  );
};

export default NotificationProvider;
